import { LogOut } from 'lucide-react';
import { useState } from 'react';
import { api } from '../../api/client.ts';
import { useProfile } from '../../api/queries.ts';
import type { AuthStatus, Brand } from '../../api/types.ts';
import { brandLogo } from '../../lib/brand.ts';
import { buttonTapped } from '../../lib/usage.ts';
import { ThemeSwitch } from './ThemeSwitch.tsx';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '../ui/dropdown-menu.tsx';

/**
 * The sign-out press, from the account card or the account menu. Ends the
 * session (POST, so no link elsewhere can do it), then reloads into the login flow.
 */
export function logout(): void {
  buttonTapped('sign_out');
  void api
    .post('/auth/logout')
    .catch(() => undefined)
    .then(() => window.location.assign('/'));
}

/** Up to two letters for the account's badge: from the name when there is one, else the address. */
export function initials(user: Pick<AuthStatus, 'email' | 'name'>): string {
  const words = (user.name ?? '').trim().split(/\s+/).filter(Boolean);
  const first = words.at(0) ?? '';
  const last = words.at(-1) ?? '';
  if (words.length >= 2) return (first.slice(0, 1) + last.slice(0, 1)).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  return user.email.slice(0, 1).toUpperCase() || '?';
}

/**
 * The account's badge: its profile photo when it has one, else its initials.
 * The session knows only the email and name, so the photo comes from the
 * profile (the same cached query the Profile page reads and a save updates).
 * Initials stand in while it loads, when it cannot be read, and when the image
 * itself fails to load.
 */
export function Monogram({ user, className = '' }: { user: AuthStatus; className?: string }) {
  const profile = useProfile();
  const photo = profile.data?.photo ?? null;
  const [broken, setBroken] = useState<string | null>(null);
  const box = `flex shrink-0 items-center justify-center rounded-full ${className}`;

  if (photo && photo !== broken) {
    return <img src={photo} alt="" aria-hidden onError={() => setBroken(photo)} className={`${box} object-cover`} />;
  }
  return (
    <span aria-hidden className={`${box} bg-brand-soft font-display font-semibold text-brand`}>
      {initials({ email: user.email, name: profile.data?.name || user.name })}
    </span>
  );
}

export function BrandMark({ brand }: { brand: Brand }) {
  return (
    <a href={brand.link} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-md">
      <img src={brandLogo(brand)} alt={brand.name} className="h-8 w-auto" />
    </a>
  );
}

/** Who is signed in, and the way out. Sits at the foot of the desktop navigation. */
export function AccountCard({ user }: { user: AuthStatus }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-surface p-3 shadow-[0_1px_0_var(--line)]">
      <Monogram user={user} className="h-10 w-10 text-sm" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">{user.name || user.email.split('@')[0]}</p>
        <p className="truncate text-xs text-muted" title={user.email}>
          {user.email}
        </p>
      </div>
      <button
        type="button"
        onClick={logout}
        className="flex h-9 w-9 items-center justify-center rounded-full text-muted transition-colors hover:bg-paper hover:text-ink"
        aria-label="Sign out"
        title="Sign out"
      >
        <LogOut className="h-[18px] w-[18px]" aria-hidden />
      </button>
    </div>
  );
}

/** The account behind a tap: who is signed in, appearance, and the way out. */
export function AccountMenu({ user, side = 'bottom', align = 'end' }: { user: AuthStatus; side?: 'bottom' | 'right'; align?: 'start' | 'end' }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="rounded-full" aria-label="Account menu" title={user.email} onClick={() => buttonTapped('account_menu')}>
        <Monogram user={user} className="h-9 w-9 text-xs" />
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align={align} className="w-64">
        <DropdownMenuLabel>
          <span className="block truncate font-semibold text-ink">{user.name || user.email.split('@')[0]}</span>
          <span className="block truncate font-normal text-muted">{user.email}</span>
        </DropdownMenuLabel>
        <ThemeSwitch className="mx-1.5 mb-1.5" />
        <DropdownMenuItem onSelect={logout}>
          <LogOut className="h-4 w-4" /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The phone's header: the brand, and the account behind a tap. */
export function TopBar({ user, brand }: { user: AuthStatus; brand: Brand }) {
  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between border-b border-line bg-surface/95 px-4 backdrop-blur md:hidden">
      <BrandMark brand={brand} />
      <AccountMenu user={user} />
    </header>
  );
}
